import { useMemo } from 'react'
import useLabelingSlice from 'redux/slices/hooks/useLabelingSlice'
import { CATEGORICAL_COLOR_PALETTE } from '../shared'

export const FALLBACK_LABEL_COLOR = '#9e9e9e'

/**
 * Maps each label to a distinct color from the shared categorical palette (the
 * same one the cluster plot uses), by its position in the session label list.
 * New labels appended to the list automatically get the next distinct color.
 */
export const getLabelColorMap = (labels: string[]): Record<string, string> => {
  const map: Record<string, string> = {}
  labels.forEach((label, index) => {
    map[label] = CATEGORICAL_COLOR_PALETTE[index % CATEGORICAL_COLOR_PALETTE.length]
  })
  return map
}

/** Picks black or white text for readable contrast against a hex background. */
export const readableTextColor = (hex: string): string => {
  const value = hex.replace('#', '')
  if (value.length < 6) return '#ffffff'
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#000000' : '#ffffff'
}

/** Label -> color map for the current session's labels. */
export const useLabelColors = (): Record<string, string> => {
  const {
    labeling: { sessionLabels },
  } = useLabelingSlice()
  return useMemo(() => getLabelColorMap(sessionLabels), [sessionLabels])
}
